import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  ShoppingBag, 
  Package, 
  Camera, 
  Search, 
  Heart, 
  MessageCircle, 
  ArrowRight,
  Sparkles,
  X,
  CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';

export default function WelcomePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const intent = searchParams.get('intent') || 'both';

  const handleGetStarted = () => {
    if (intent === 'buyer') {
      navigate('/create-wtb');
    } else if (intent === 'seller') {
      navigate('/list/photos');
    } else {
      navigate('/dashboard');
    }
  };

  const handleSkip = () => {
    navigate('/dashboard');
  };

  // Content for buyers
  const BuyerContent = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 rounded-2xl bg-[#C9956C]/10 flex items-center justify-center mx-auto mb-4">
          <ShoppingBag className="w-8 h-8 text-[#C9956C]" />
        </div>
        <h2 className="text-2xl font-bold text-[#1E3A5F] mb-2">
          How to Find What You Want
        </h2>
        <p className="text-[#6B7280]">
          Stop searching through endless listings. Let sellers come to you!
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex items-start gap-4 p-4 rounded-xl bg-[#F8F5F0]">
          <div className="w-10 h-10 rounded-full bg-[#C9956C] flex items-center justify-center flex-shrink-0 text-white font-bold">
            1
          </div>
          <div>
            <h3 className="font-bold text-[#1E3A5F]">Post a "Want to Buy" Ad</h3>
            <p className="text-sm text-[#6B7280] mt-1">
              Describe what you're looking for, set your budget range, and choose how flexible you are on specifics.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-4 p-4 rounded-xl bg-[#F8F5F0]">
          <div className="w-10 h-10 rounded-full bg-[#C9956C] flex items-center justify-center flex-shrink-0 text-white font-bold">
            2
          </div>
          <div>
            <h3 className="font-bold text-[#1E3A5F]">Get Matched with Sellers</h3>
            <p className="text-sm text-[#6B7280] mt-1">
              Our smart matching finds nearby sellers with what you want. You'll be notified when there's a match!
            </p>
          </div>
        </div>

        <div className="flex items-start gap-4 p-4 rounded-xl bg-[#F8F5F0]">
          <div className="w-10 h-10 rounded-full bg-[#C9956C] flex items-center justify-center flex-shrink-0 text-white font-bold">
            3
          </div>
          <div>
            <h3 className="font-bold text-[#1E3A5F]">Chat & Complete the Deal</h3>
            <p className="text-sm text-[#6B7280] mt-1">
              Unlock matches to chat directly with sellers. Arrange meetup, inspect the item, and complete your purchase.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-[#1E3A5F]/5 rounded-xl p-4 border border-[#1E3A5F]/10">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-[#C9956C] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-[#1E3A5F] font-medium">Pro Tip: Verify with Proof of Funds</p>
            <p className="text-xs text-[#6B7280] mt-1">
              Stand out as a serious buyer! Add a refundable deposit to get priority matching and a verified badge.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // Content for sellers
  const SellerContent = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 rounded-2xl bg-[#1E3A5F]/10 flex items-center justify-center mx-auto mb-4">
          <Package className="w-8 h-8 text-[#1E3A5F]" />
        </div>
        <h2 className="text-2xl font-bold text-[#1E3A5F] mb-2">
          How to Sell Your Items
        </h2>
        <p className="text-[#6B7280]">
          List your items and let buyers find you!
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex items-start gap-4 p-4 rounded-xl bg-[#F8F5F0]">
          <div className="w-10 h-10 rounded-full bg-[#1E3A5F] flex items-center justify-center flex-shrink-0 text-white font-bold">
            1
          </div>
          <div>
            <h3 className="font-bold text-[#1E3A5F]">Add Items with AI Photo Scanning</h3>
            <p className="text-sm text-[#6B7280] mt-1">
              Simply take photos of your items! Our AI identifies them, estimates value, and creates listings automatically.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-4 p-4 rounded-xl bg-[#F8F5F0]">
          <div className="w-10 h-10 rounded-full bg-[#1E3A5F] flex items-center justify-center flex-shrink-0 text-white font-bold">
            2
          </div>
          <div>
            <h3 className="font-bold text-[#1E3A5F]">Choose Public or Passive Mode</h3>
            <p className="text-sm text-[#6B7280] mt-1">
              <strong>Public:</strong> Actively listed for sale. <strong>Passive:</strong> Only shows when someone's looking for it.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-4 p-4 rounded-xl bg-[#F8F5F0]">
          <div className="w-10 h-10 rounded-full bg-[#1E3A5F] flex items-center justify-center flex-shrink-0 text-white font-bold">
            3
          </div>
          <div>
            <h3 className="font-bold text-[#1E3A5F]">Get Matched & Sell</h3>
            <p className="text-sm text-[#6B7280] mt-1">
              When a buyer wants what you have, you'll be notified. Chat, arrange meetup, and make the sale!
            </p>
          </div>
        </div>
      </div>

      <div className="bg-[#059669]/5 rounded-xl p-4 border border-[#059669]/10">
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-[#059669] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-[#1E3A5F] font-medium">Zero Seller Fees!</p>
            <p className="text-xs text-[#6B7280] mt-1">
              Sellers never pay fees on WANTD. Only a small fee is charged to buyers when a transaction completes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // Content for both
  const BothContent = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 rounded-2xl bg-[#059669]/10 flex items-center justify-center mx-auto mb-4">
          <Heart className="w-8 h-8 text-[#059669]" />
        </div>
        <h2 className="text-2xl font-bold text-[#1E3A5F] mb-2">
          Welcome to WANTD!
        </h2>
        <p className="text-[#6B7280]">
          Buy what you need, sell what you don't. Here's how it works.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Buying */}
        <div className="p-5 rounded-xl border border-[#E5DDD3] bg-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#C9956C]/10 flex items-center justify-center">
              <ShoppingBag className="w-5 h-5 text-[#C9956C]" />
            </div>
            <h3 className="font-bold text-[#1E3A5F]">Buying</h3>
          </div>
          <ul className="space-y-2 text-sm text-[#6B7280]">
            <li className="flex items-start gap-2">
              <Search className="w-4 h-4 text-[#C9956C] flex-shrink-0 mt-0.5" />
              Post what you want to buy
            </li>
            <li className="flex items-start gap-2">
              <Heart className="w-4 h-4 text-[#C9956C] flex-shrink-0 mt-0.5" />
              Get matched with local sellers
            </li>
            <li className="flex items-start gap-2">
              <MessageCircle className="w-4 h-4 text-[#C9956C] flex-shrink-0 mt-0.5" />
              Chat and complete deals
            </li>
          </ul>
          <Button 
            variant="outline"
            onClick={() => navigate('/create-wtb')}
            className="w-full mt-4 rounded-full border-[#C9956C] text-[#C9956C] hover:bg-[#C9956C]/10"
          >
            Add a Want
          </Button>
        </div>

        {/* Selling */}
        <div className="p-5 rounded-xl border border-[#E5DDD3] bg-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#1E3A5F]/10 flex items-center justify-center">
              <Package className="w-5 h-5 text-[#1E3A5F]" />
            </div>
            <h3 className="font-bold text-[#1E3A5F]">Selling</h3>
          </div>
          <ul className="space-y-2 text-sm text-[#6B7280]">
            <li className="flex items-start gap-2">
              <Camera className="w-4 h-4 text-[#1E3A5F] flex-shrink-0 mt-0.5" />
              Photo scan your items with AI
            </li>
            <li className="flex items-start gap-2">
              <Package className="w-4 h-4 text-[#1E3A5F] flex-shrink-0 mt-0.5" />
              Build your passive inventory
            </li>
            <li className="flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-[#1E3A5F] flex-shrink-0 mt-0.5" />
              Sell when someone wants it
            </li>
          </ul>
          <Button 
            variant="outline"
            onClick={() => navigate('/list/photos')}
            className="w-full mt-4 rounded-full border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F]/10"
          >
            List Items
          </Button>
        </div>
      </div>

      <div className="bg-[#F5F0E8] rounded-xl p-4">
        <p className="text-sm text-[#6B7280] text-center">
          <strong className="text-[#1E3A5F]">Remember:</strong> You can always switch between buying and selling - no restrictions!
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8F5F0] flex items-center justify-center px-4 py-12 relative">
      {/* Close/Skip Button */}
      <button
        onClick={handleSkip}
        className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white border border-[#E5DDD3] flex items-center justify-center hover:bg-[#F5F0E8] transition-colors shadow-sm z-10"
        title="Skip to Dashboard"
      >
        <X className="w-5 h-5 text-[#6B7280]" />
      </button>

      <div className="w-full max-w-xl">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-12 h-12 rounded-xl bg-[#1E3A5F] flex items-center justify-center">
            <ShoppingBag className="w-6 h-6 text-white" />
          </div>
          <span className="text-2xl font-black tracking-tight text-[#1E3A5F]">WANTD</span>
        </Link>

        {/* Welcome message */}
        {user && (
          <p className="text-center text-[#6B7280] mb-6">
            Welcome, <span className="font-medium text-[#1E3A5F]">{user.username}</span>! 🎉
          </p>
        )}

        {/* Card */}
        <div className="bg-white border border-[#E5DDD3] rounded-2xl p-6 md:p-8 shadow-card">
          {intent === 'buyer' && <BuyerContent />}
          {intent === 'seller' && <SellerContent />}
          {intent === 'both' && <BothContent />}

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Button
              variant="outline"
              onClick={handleSkip}
              className="flex-1 rounded-full border-[#E5DDD3] text-[#6B7280]"
            >
              Skip for Now
            </Button>
            <Button
              onClick={handleGetStarted}
              className="flex-1 rounded-full bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white"
            >
              Get Started
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
